import { type UseFormReturn, useForm } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useWatch } from 'react-hook-form';
import {
  generateBulkCreationRequest,
  UserBulkCreationFormValues,
  userBulkCreationSchema,
} from '../logics';
import { useBulkCreationUser } from './useBulkCreationUser';
import { useGetUsersCsv } from './useGetUsersCsv';

type Args = {
  onSuccess: () => void;
};

type IUseUserBulkCreation = (args: Args) => {
  state: {
    methods: UseFormReturn<UserBulkCreationFormValues>;
    registrationType: 'immediate' | 'scheduled';
    isConfirmDisabled: boolean;
  };
  actions: {
    handleSubmit: () => void;
    handleDownloadCsvTemplate: () => void;
    handleDownloadCsvTemplateSjis: () => void;
  };
};

/**
 * CSV 一括追加モーダルの状態管理とアクションを提供するフック
 *
 * - RHF + Zod でフォームのバリデーションを管理する
 * - registrationType が 'scheduled' のときのみ日時フィールドを有効化する
 */
export const useUserBulkCreation: IUseUserBulkCreation = ({ onSuccess }) => {
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() ?? '';

  const methods = useForm<UserBulkCreationFormValues>({
    defaultValues: {
      registrationType: 'immediate',
      file: undefined,
      scheduledDate: undefined,
      scheduledTime: undefined,
    },
    resolver: zodResolver(userBulkCreationSchema),
    mode: 'onChange',
  });

  const registrationType = useWatch({ control: methods.control, name: 'registrationType' });

  const { trigger } = useBulkCreationUser(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message:
          '利用者の一括追加を受け付けました。\n「登録予約一覧」タブよりステータスを確認できます。\n※すべての利用者の登録には最大10分程度かかる場合がございます。',
        status: 'success',
      });
      onSuccess();
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({ message: errors[0], status: 'danger' });
          return;
        }
      }
      showToast({ message: '利用者の一括追加に失敗しました。', status: 'danger' });
    },
  });

  const { trigger: downloadUsersCsv } = useGetUsersCsv(threeLetterCode, {
    onSuccess: blob => downloadFile(blob, 'users_utf.csv'),
    onError: () => showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' }),
  });

  const { trigger: downloadUsersCsvSjis } = useGetUsersCsv(threeLetterCode, {
    encoding: 'sjis',
    onSuccess: blob => downloadFile(blob, 'users-sjis.csv'),
    onError: () => showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' }),
  });

  const handleSubmit = methods.handleSubmit(data => {
    trigger(generateBulkCreationRequest(data));
  });

  /**
   * テンプレート CSV（UTF-8）ダウンロードボタン押下時のハンドラ
   *
   * テンプレ専用 API ではなく利用者一覧 CSV API（/users.csv）を共用しているため、
   * ファイル名は `users_utf.csv` に統一する。
   */
  const handleDownloadCsvTemplate = () => {
    downloadUsersCsv();
  };

  /**
   * テンプレート CSV（Shift-JIS）ダウンロードボタン押下時のハンドラ
   *
   * `?encoding=sjis` クエリを付与した利用者一覧 CSV API（/users.csv?encoding=sjis）を使用する。
   * ファイル名は API 仕様準拠で `users-sjis.csv`（ハイフン区切り）。
   */
  const handleDownloadCsvTemplateSjis = () => {
    downloadUsersCsvSjis();
  };

  return {
    state: {
      methods,
      registrationType,
      isConfirmDisabled: !methods.formState.isValid,
    },
    actions: {
      handleSubmit,
      handleDownloadCsvTemplate,
      handleDownloadCsvTemplateSjis,
    },
  };
};
